import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  siteDataApi,
  type DeliveryTargetPeriod,
  type DeliveryTargetChannelRow,
  type DeliveryTargetPersonRow,
} from '../api/siteData';
import { ReportExportButton } from '../components/ReportExportButton';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import '../components/DataUpdateNote.css';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useLazySection } from '../hooks/useLazySection';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric } from '../lib/siteDataSecurity';
import { SortableHeader, sortRows, useTableSort, type SortValue } from '../components/SortableTable';

const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtNum = (v: number) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// 周期首日（month=月首，quarter=季首，year=年首）
function periodFirstDay(period: DeliveryTargetPeriod, anchor: Date): string {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (period === 'quarter') return fmtLocalDate(new Date(y, Math.floor(m / 3) * 3, 1));
  if (period === 'year') return fmtLocalDate(new Date(y, 0, 1));
  return fmtLocalDate(new Date(y, m, 1));
}

function previousPeriodFirstDay(period: DeliveryTargetPeriod, anchor: Date): string {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (period === 'quarter') return fmtLocalDate(new Date(y, Math.floor(m / 3) * 3 - 3, 1));
  if (period === 'year') return fmtLocalDate(new Date(y - 1, 0, 1));
  return fmtLocalDate(new Date(y, m - 1, 1));
}

const PERIOD_TABS: { value: DeliveryTargetPeriod; label: string }[] = [
  { value: 'month', label: '月' }, { value: 'quarter', label: '季度' }, { value: 'year', label: '年' },
];

type DatePreset = 'current' | 'previous' | 'custom';

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'current', label: '本期' },
  { value: 'previous', label: '上期' },
  { value: 'custom', label: '自定义' },
];

const tdNum: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const thBase: React.CSSProperties = { background: 'var(--surface2)', whiteSpace: 'nowrap' };

function Card({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        {extra}
      </div>
      {children}
    </div>
  );
}

// 渠道表列定义（顺序对照截图）
type DeliveryTargetColumn<T> = { key: keyof T; label: string; fmt: (v: number) => string };

const CHANNEL_COLS: DeliveryTargetColumn<DeliveryTargetChannelRow>[] = [
  { key: 'sales_target', label: '销售额目标', fmt: fmtNum },
  { key: 'revenue', label: '购物转化价值', fmt: fmtNum },
  { key: 'progress', label: '完成进度', fmt: fmtPct },
  { key: 'spend', label: '账户花费', fmt: fmtNum },
  { key: 'order_num', label: '支付订单数', fmt: fmtInt },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum },
  { key: 'roi', label: '账户ROI', fmt: fmtNum },
  { key: 'avg_price', label: '账户单均价', fmt: fmtNum },
  { key: 'buy_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum },
  { key: 'new_spend', label: '账户新客花费', fmt: fmtNum },
  { key: 'session_num', label: '神策会话数', fmt: fmtInt },
  { key: 'sce_order_num', label: '神策订单量', fmt: fmtInt },
  { key: 'session_cost', label: '神策会话成本', fmt: fmtNum },
  { key: 'arrival_rate', label: '到达率', fmt: fmtPct },
  { key: 'sce_conv_rate', label: '神策转化率', fmt: fmtPct },
  { key: 'sce_cpa', label: '神策CPA', fmt: fmtNum },
  { key: 'sce_roi', label: '神策ROI', fmt: fmtNum },
  { key: 'sce_sales', label: '神策销售额', fmt: fmtNum },
  { key: 'user_ct', label: '获客数', fmt: fmtInt },
  { key: 'new_sales', label: '新客销售额', fmt: fmtNum },
  { key: 'new_order_num', label: '新客订单量', fmt: fmtInt },
  { key: 'new_user_num', label: '新客数', fmt: fmtInt },
  { key: 'new_user_rate', label: '新客占比', fmt: fmtPct },
  { key: 'new_avg_price', label: '新客单均价', fmt: fmtNum },
  { key: 'new_acq_cost', label: '新客获客成本', fmt: fmtNum },
];

// 人员表列定义
const PERSON_COLS: DeliveryTargetColumn<DeliveryTargetPersonRow>[] = [
  { key: 'sales_target', label: '销售额目标', fmt: fmtNum },
  { key: 'revenue', label: '购物转化价值', fmt: fmtNum },
  { key: 'progress', label: '销售额完成进度', fmt: fmtPct },
  { key: 'spend', label: '账户花费', fmt: fmtNum },
  { key: 'order_num', label: '支付订单数', fmt: fmtInt },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum },
  { key: 'roi', label: '账户ROI', fmt: fmtNum },
  { key: 'avg_price', label: '账户单均价', fmt: fmtNum },
  { key: 'buy_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum },
];

const TOTAL_ROW_LABELS = new Set(['总计', '合计', '小计']);

function isTotalLabel(value: string | undefined): boolean {
  return !!value && TOTAL_ROW_LABELS.has(value.trim());
}

function isChannelTotalRow(row: DeliveryTargetChannelRow): boolean {
  return isTotalLabel(row.channel);
}

function isPersonTotalRow(row: DeliveryTargetPersonRow): boolean {
  return isTotalLabel(row.person);
}

function channelSortValue(row: DeliveryTargetChannelRow, key: string): SortValue {
  return row[key as keyof DeliveryTargetChannelRow] as SortValue;
}

function personSortValue(row: DeliveryTargetPersonRow, key: string): SortValue {
  return row[key as keyof DeliveryTargetPersonRow] as SortValue;
}

export default function SiteDeliveryTarget() {
  const { brand } = useSiteShopName();
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const isVooglam = brand.toLowerCase() === 'vooglam';
  const [period, setPeriod] = useState<DeliveryTargetPeriod>('month');
  const [startDate, setStartDate] = useState(() => periodFirstDay('month', new Date()));
  const [endDate, setEndDate] = useState(() => periodFirstDay('month', new Date()));
  const [appliedDate, setAppliedDate] = useState(() => ({ period: 'month' as DeliveryTargetPeriod, startDate: periodFirstDay('month', new Date()), endDate: periodFirstDay('month', new Date()) }));
  const [channelExportDate, setChannelExportDate] = useState<typeof appliedDate | null>(null);
  const [personExportDate, setPersonExportDate] = useState<typeof appliedDate | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('current');

  const [channel, setChannel] = useState<DeliveryTargetChannelRow[]>([]);
  const [person, setPerson] = useState<DeliveryTargetPersonRow[]>([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [personLoading, setPersonLoading] = useState(false);
  const [error, setError] = useState('');

  const channelTotalRows = useMemo(() => channel.filter(isChannelTotalRow), [channel]);
  const channelDetailRows = useMemo(() => channel.filter(row => !isChannelTotalRow(row)), [channel]);
  const {
    sort: channelSort,
    toggleSort: toggleChannelSort,
    sortedRows: sortedChannelDetailRows,
    setSort: setChannelSort,
  } = useTableSort<DeliveryTargetChannelRow, string>(channelDetailRows, channelSortValue);
  const sortedChannelRows = useMemo(
    () => [...channelTotalRows, ...sortedChannelDetailRows],
    [channelTotalRows, sortedChannelDetailRows],
  );

  const {
    sort: personSort,
    toggleSort: togglePersonSort,
    setSort: setPersonSort,
  } = useTableSort<DeliveryTargetPersonRow, string>(person, personSortValue);

  useEffect(() => {
    if (channelSort && userLevel < 2 && isSiteSensitiveMetric(channelSort.key)) setChannelSort(null);
  }, [channelSort, userLevel, setChannelSort]);

  useEffect(() => {
    if (personSort && userLevel < 2 && isSiteSensitiveMetric(personSort.key)) setPersonSort(null);
  }, [personSort, userLevel, setPersonSort]);

  const onPeriod = (p: DeliveryTargetPeriod) => {
    setPeriod(p);
    setDatePreset('current');
    const current = periodFirstDay(p, new Date());
    setStartDate(current);
    setEndDate(current);
  };

  const onDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset === 'current') {
      const current = periodFirstDay(period, new Date());
      setStartDate(current);
      setEndDate(current);
    } else if (preset === 'previous') {
      const previous = previousPeriodFirstDay(period, new Date());
      setStartDate(previous);
      setEndDate(previous);
    }
  };

  const personSection = useLazySection(!isVooglam);

  const fetchChannel = useCallback(async () => {
    if (isVooglam) {
      setChannel([]);
      setError('');
      setChannelLoading(false);
      return;
    }
    setChannelLoading(true); setError('');
    try {
      const c = await siteDataApi.getDeliveryTargetChannel({ period: appliedDate.period, start_date: appliedDate.startDate, end_date: appliedDate.endDate });
      setChannel(c.data?.list ?? []);
      setChannelExportDate({ ...appliedDate });
    } catch (e) {
      setChannelExportDate(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setChannelLoading(false);
    }
  }, [appliedDate, isVooglam]);

  const fetchPerson = useCallback(async () => {
    if (isVooglam) {
      setPerson([]);
      setPersonLoading(false);
      return;
    }
    setPersonLoading(true); setError('');
    try {
      const pp = await siteDataApi.getDeliveryTargetPerson({ period: appliedDate.period, start_date: appliedDate.startDate, end_date: appliedDate.endDate });
      setPerson(pp.data?.list ?? []);
      setPersonExportDate({ ...appliedDate });
    } catch (e) {
      setPersonExportDate(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setPersonLoading(false);
    }
  }, [appliedDate, isVooglam]);

  useEffect(() => { void fetchChannel(); }, [fetchChannel]);
  useEffect(() => { if (personSection.ready) void fetchPerson(); }, [personSection.ready, fetchPerson]);

  const query = () => setAppliedDate({ period, startDate, endDate });
  useMarketingViewSection('page', {
    label: '页面筛选', state: { period, startDate, endDate, datePreset },
    restore: value => { setPeriod(value.period); setStartDate(value.startDate); setEndDate(value.endDate); setDatePreset(value.datePreset); },
    loading: channelLoading || personLoading, query: () => { query(); },
    resolve: value => { if (value.datePreset === 'custom') return value; const date = value.datePreset === 'previous' ? previousPeriodFirstDay(value.period, new Date()) : periodFirstDay(value.period, new Date()); return { ...value, startDate: date, endDate: date }; },
    summary: startDate + ' ～ ' + endDate,
  });

  const reset = () => {
    const date = periodFirstDay('month', new Date());
    setPeriod('month');
    setDatePreset('current');
    setStartDate(date);
    setEndDate(date);
    setAppliedDate({ period: 'month', startDate: date, endDate: date });
  };

  // 人员表：按平台分组（首行显示平台，rowspan 效果用单元格合并）
  const personGroups = useMemo(() => {
    const map = new Map<string, DeliveryTargetPersonRow[]>();
    for (const r of person) {
      const arr = map.get(r.platform) ?? [];
      arr.push(r);
      map.set(r.platform, arr);
    }
    const groups = Array.from(map.entries()).map(([platform, rows]) => {
      const totalRows = rows.filter(isPersonTotalRow);
      const detailRows = rows.filter(row => !isPersonTotalRow(row));
      return [platform, [...totalRows, ...sortRows(detailRows, personSort, personSortValue)]] as [string, DeliveryTargetPersonRow[]];
    });
    return personSort?.key === 'platform'
      ? sortRows(groups, personSort, ([platform]) => platform)
      : groups;
  }, [person, personSort]);

  if (isVooglam) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>当前品牌暂无投放目标跟进看板</div>
          <div style={{ fontSize: 13 }}>该报表仅适用于 Zeelool，Vooglam 暂未在 BI 中配置此报表。</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      {/* 周期 + 日期 */}
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter
            grain={period}
            grains={PERIOD_TABS}
            onGrainChange={onPeriod}
            preset={datePreset}
            presets={DATE_PRESETS}
            onPresetChange={onDatePreset}
            start={startDate}
            end={endDate}
            max={fmtLocalDate(new Date())}
            customPreset="custom"
            onRangeChange={({ start, end }) => { setStartDate(start); setEndDate(end); }}
            grainLabel="日期"
            rangeLabel=""
            variant="reference"
            alwaysShowDatePicker
          />
          <DashboardQueryActions loading={channelLoading || personLoading} onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>{error}</div>
      )}

      <aside className="data-update-note" aria-labelledby="delivery-target-data-note-title">
        <h2 id="delivery-target-data-note-title">数据更新说明</h2>
        <div className="data-update-note-content">
          <p>1、数据更新频率：每天更新月度数据，当月展示的是 MTD 数据；每月 1 日更新季度和年度数据。</p>
          <p>2、月度报表、季度报表、年度报表里的新客都是指的月度新客。</p>
          <p>3、神策相关数据，用的是每笔订单实际的末次归因渠道。</p>
        </div>
      </aside>

      {/* 图1: 渠道完成进度跟进 */}
      <Card
        title="渠道完成进度跟进"
        extra={<ReportExportButton reportKey="site_delivery_target_channel" title="渠道完成进度跟进" fileName={`渠道完成进度跟进_${channelExportDate?.period ?? ''}_${channelExportDate?.startDate ?? ''}_${channelExportDate?.endDate ?? ''}.csv`} params={{ period: channelExportDate?.period ?? '', date: channelExportDate?.startDate ?? '', start_date: channelExportDate?.startDate ?? '', end_date: channelExportDate?.endDate ?? '' }} columns={[{ key: 'channel', label: '渠道' }, ...CHANNEL_COLS.map(c => ({ key: c.key as string, label: c.label }))]} disabled={!channelExportDate} />}
      >
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 600, overflow: 'auto' }}>
          <table style={{ minWidth: 2600 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <SortableHeader sortKey="channel" sort={channelSort} onSort={toggleChannelSort} style={{ ...thBase, position: 'sticky', left: 0, zIndex: 3 }}>渠道</SortableHeader>
                {CHANNEL_COLS.map(c => (
                  <SortableHeader
                    key={c.key}
                    sortKey={String(c.key)}
                    sort={channelSort}
                    onSort={toggleChannelSort}
                    align="right"
                    style={{ ...thBase, ...tdNum }}
                    disabled={userLevel < 2 && isSiteSensitiveMetric(String(c.key))}
                  >
                    <MetricDescriptionTooltip label={c.label} />
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {channelLoading && channel.length === 0 ? <tr><td colSpan={CHANNEL_COLS.length + 1}><TableLoadingCell /></td></tr>
                : channel.length === 0 ? <tr><td colSpan={CHANNEL_COLS.length + 1} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
                : sortedChannelRows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--surface)', fontWeight: 600 }}>{r.channel}</td>
                    {CHANNEL_COLS.map(c => <td key={c.key} style={tdNum}>{formatSiteMetric(c.key as string, r[c.key] as number, c.fmt, userLevel)}</td>)}
                  </tr>
                ))}
            </tbody>
          </table>
          {channelLoading && channel.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>

      {/* 图2: 人员完成进度跟进 */}
      <div ref={personSection.ref}>
      <Card
        title="人员完成进度跟进"
        extra={<ReportExportButton reportKey="site_delivery_target_person" title="人员完成进度跟进" fileName={`人员完成进度跟进_${personExportDate?.period ?? ''}_${personExportDate?.startDate ?? ''}_${personExportDate?.endDate ?? ''}.csv`} params={{ period: personExportDate?.period ?? '', date: personExportDate?.startDate ?? '', start_date: personExportDate?.startDate ?? '', end_date: personExportDate?.endDate ?? '' }} columns={[{ key: 'platform', label: '平台' }, { key: 'person', label: '人员' }, ...PERSON_COLS.map(c => ({ key: c.key as string, label: c.label }))]} disabled={!personExportDate} />}
      >
        <div className="table-wrap" style={{ position: 'relative', maxHeight: 600, overflow: 'auto' }}>
          <table style={{ minWidth: 1300 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <SortableHeader sortKey="platform" sort={personSort} onSort={togglePersonSort} style={thBase}>平台</SortableHeader>
                <SortableHeader sortKey="person" sort={personSort} onSort={togglePersonSort} style={thBase}>人员</SortableHeader>
                {PERSON_COLS.map(c => (
                  <SortableHeader
                    key={c.key}
                    sortKey={String(c.key)}
                    sort={personSort}
                    onSort={togglePersonSort}
                    align="right"
                    style={{ ...thBase, ...tdNum }}
                    disabled={userLevel < 2 && isSiteSensitiveMetric(String(c.key))}
                  >
                    <MetricDescriptionTooltip label={c.label} />
                  </SortableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {personLoading && person.length === 0 ? <tr><td colSpan={PERSON_COLS.length + 2}><TableLoadingCell /></td></tr>
                : person.length === 0 ? <tr><td colSpan={PERSON_COLS.length + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
                : personGroups.map(([platform, rows]) =>
                  rows.map((r, j) => (
                    <tr key={platform + j}>
                      {j === 0 && <td rowSpan={rows.length} style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', fontWeight: 600, background: 'var(--surface2)' }}>{platform}</td>}
                      <td style={{ whiteSpace: 'nowrap' }}>{r.person}</td>
                      {PERSON_COLS.map(c => <td key={c.key} style={tdNum}>{formatSiteMetric(c.key as string, r[c.key] as number, c.fmt, userLevel)}</td>)}
                    </tr>
                  ))
                )}
            </tbody>
          </table>
          {personLoading && person.length > 0 && <TableLoadingOverlay />}
        </div>
      </Card>
      </div>
    </div>
  );
}
